import { ChevronRight, Save } from 'lucide-react';
import SSEBuilder from '../SSEBuilder';
import RequestKindIcon from '../ui/RequestKindIcon';

/** The SSE request builder (plain render function, called where the markup was). */
export function renderSseBuilder({
  collectionName, t, nameSlotWidthCh, editingRequestName, nameInputRef, name, setName,
  emitDataChange, method, url, headers, params, cookies, body, bodyType, auth,
  commitRequestNameEdit, nameSnapshotRef, setEditingRequestName, breadcrumbDisplayLabel, readOnly,
  saveRequest, initialData, tabFieldsRef, selectedEnvironment, workspaceVariables, globalVariables,
  collectionVariables, folderVariables,
}) {
  return (
    <div className="h-full bg-gray-50 flex flex-col">
      {/* No bottom padding / border here: the request bar (StreamRequestBar) continues this block,
          as the URL bar does INSIDE the HTTP header — the gap title → field was 26 px instead of 14. */}
      <div className="flex-shrink-0 bg-white px-5 stream-head" style={{ paddingTop: 12 }}>
        <div className="editor-header-toolbar editor-header-toolbar--request">
          <div className="flex items-center min-w-0 flex-1 gap-2 text-xs">
            <span className="request-kind-head-icon" role="img" aria-label="SSE request"><RequestKindIcon optionId="sse" size={16} /></span>
            <span className="text-gray-500 truncate cursor-pointer" title={collectionName || undefined}>
              {collectionName ?? t('request.noCollection')}
            </span>
            <ChevronRight size={14} strokeWidth={2} className="text-gray-400 flex-shrink-0 opacity-90" aria-hidden />
            <span
              className="inline-flex items-center justify-start min-w-0 max-w-full"
              style={{ width: `${nameSlotWidthCh}ch`, maxWidth: 'min(28rem, 100%)' }}
            >
              {editingRequestName ? (
                <input
                  ref={nameInputRef}
                  type="text"
                  value={name}
                  placeholder={t('request.newRequest')}
                  onChange={(e) => {
                    const next = e.target.value;
                    setName(next);
                    emitDataChange({ name: next, method, url, headers, params, cookies, body, bodyType, auth });
                  }}
                  onBlur={commitRequestNameEdit}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') { e.preventDefault(); commitRequestNameEdit(); }
                    if (e.key === 'Escape') {
                      e.preventDefault();
                      const prev = nameSnapshotRef.current;
                      setName(prev);
                      emitDataChange({ name: prev, method, url, headers, params, cookies, body, bodyType, auth });
                      setEditingRequestName(false);
                    }
                  }}
                  className="request-breadcrumb-name-input request-breadcrumb-name-slot-inner w-full min-w-0 text-gray-900 font-semibold max-w-full"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => { nameSnapshotRef.current = name; setEditingRequestName(true); }}
                  className="request-breadcrumb-name-slot-inner w-full min-w-0 font-semibold text-gray-900 truncate text-left bg-transparent border-none cursor-text m-0 hover:opacity-90 max-w-full"
                  title={t('request.editRequestName')}
                >
                  {breadcrumbDisplayLabel}
                </button>
              )}
            </span>
          </div>
          {!readOnly && (
          <button
            type="button"
            onClick={saveRequest}
            className="request-header-save inline-flex items-center gap-2 px-3 py-1 rounded-md bg-transparent text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors text-xs font-medium flex-shrink-0"
            title={t('request.saveRequest')}
          >
            <Save size={14} strokeWidth={2} />
            <span className="hidden sm:inline">{t('request.save')}</span>
          </button>
          )}
        </div>
      </div>
      <div className="flex-1 overflow-hidden" style={{ minHeight: 0 }}>
        <SSEBuilder
          request={initialData}
          onDataChange={(patch) => emitDataChange({ ...tabFieldsRef.current, ...patch })}
          selectedEnvironment={selectedEnvironment}
          workspaceVariables={workspaceVariables}
          globalVariables={globalVariables}
          collectionVariables={collectionVariables}
          folderVariables={folderVariables}
        />
      </div>
    </div>
  );
}
